import { computed, reactive, ref, toRaw } from 'vue'
import { useNuxtApp } from '#app'
import { result, omit, isEqual, debounce, cloneDeep } from 'lodash-es'

type PaginationResponse<TItem> = {
  items: TItem[]
  itemCount: number
  pageCount: number
}

type PaginationFilter = {
  page?: number;
  pageSize?: number
} & Record<string, any>

export function useList<TItem, TFilter extends PaginationFilter>(
  options: {
    endpoint: string
    defaultFilter: TFilter | (() => TFilter)
    normalize?: (response: any) => PaginationResponse<TItem>
  }) {

  const { $api } = useNuxtApp()

  const filter = reactive(cloneDeep(result<TFilter>(options, 'defaultFilter')))

  const items = ref<TItem[]>([])
  const itemCount = ref(0)
  const pageCount = ref(0)

  const firstLoading = ref(true)
  const listLoading = ref(false)

  const page = computed(() => Number(filter.page ?? 1))
  const pageSize = computed(() => Number(filter.pageSize ?? 0))

  const isEmpty = computed(() => itemCount.value == 0 && !firstLoading.value)

  const isSearchEmpty = computed(() => {
    if (listLoading.value || items.value.length > 0) return false
    const currentFilter = omit(toRaw(filter), ['page', 'pageSize'])
    const defaultFilter = result<TFilter>(options, 'defaultFilter')
    const defaults = omit(defaultFilter, ['page', 'pageSize'])
    return !isEqual(currentFilter, defaults)
  })

  const firstItemOfPage = computed(() => {
    if (!itemCount.value) return 0
    return (page.value - 1) * pageSize.value + 1
  })

  const lastItemOfPage = computed(() => {
    return (page.value - 1) * pageSize.value + (items.value?.length || 0)
  })

  let controller: AbortController | null = null

  async function load() {
    if (controller) controller.abort()
    controller = new AbortController()
    const signal = controller.signal

    try {
      listLoading.value = true
      const response = await $api(options.endpoint, { method: 'GET', query: toRaw(filter), signal })
      const normalized = options.normalize ? options.normalize(response) : (response as PaginationResponse<TItem>)

      items.value = normalized.items ?? []
      itemCount.value = normalized.itemCount ?? 0
      pageCount.value = normalized.pageCount ?? 0
    } catch (error: any) {
      if (error.name === 'AbortError') return
      throw error
    } finally {
      if (!signal.aborted) {
        listLoading.value = false
        firstLoading.value = false
      }
    }
  }

  async function search() {
    filter.page = 1
    await load()
  }

  const searchDebounced = debounce(search, 300)

  function reset() {
    Object.keys(filter).forEach((k) => delete (filter as any)[k])
    Object.assign(filter, result(options, 'defaultFilter'))

    items.value = []
    itemCount.value = 0
    pageCount.value = 0

    firstLoading.value = true
    listLoading.value = false
  }

  return {
    filter,
    items,
    itemCount,
    pageCount,
    firstItemOfPage,
    lastItemOfPage,
    firstLoading,
    listLoading,
    isEmpty,
    isSearchEmpty,
    load,
    reset,

    search,
    searchDebounced,
  }
}

export type ListResult<TItem, TFilter extends PaginationFilter> = ReturnType<typeof useList<TItem, TFilter>>
